import { useState, useEffect, useCallback, useRef } from 'react';
import i18n from 'i18next';

// Интерфейсы для кэширования
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live в миллисекундах
}

interface CacheConfig {
  defaultTTL?: number; // По умолчанию 5 минут
  maxSize?: number; // Максимальный размер кэша
  enableAutoRefresh?: boolean; // Автообновление при истечении TTL
}

interface UseAnalyticsCacheOptions<T> {
  key: string;
  fetcher: () => Promise<T>;
  config?: CacheConfig;
  dependencies?: any[]; // Зависимости для пересоздания кэша
}

// Глобальный кэш для всех аналитических данных
const globalCache = new Map<string, CacheEntry<any>>();

// Конфигурация по умолчанию
const DEFAULT_CONFIG: Required<CacheConfig> = {
  defaultTTL: 5 * 60 * 1000, // 5 минут
  maxSize: 100, // Максимум 100 записей
  enableAutoRefresh: true
};

/**
 * Hook для кэширования аналитических данных
 * Обеспечивает автоматическое управление кэшем, TTL и автообновление
 */
export function useAnalyticsCache<T>({
  key,
  fetcher,
  config = {},
  dependencies = []
}: UseAnalyticsCacheOptions<T>) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  
  const configRef = useRef({ ...DEFAULT_CONFIG, ...config });
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Проверка валидности кэша
  const isCacheValid = useCallback((entry: CacheEntry<T>): boolean => {
    const now = Date.now();
    return (now - entry.timestamp) < entry.ttl;
  }, []);

  // Очистка устаревших записей
  const cleanupExpiredEntries = useCallback(() => {
    const now = Date.now();
    Array.from(globalCache.entries()).forEach(([cacheKey, entry]) => {
      if ((now - entry.timestamp) >= entry.ttl) {
        globalCache.delete(cacheKey);
      }
    });
  }, []);

  // Ограничение размера кэша
  const enforceMaxSize = useCallback(() => {
    if (globalCache.size > configRef.current.maxSize) {
      const entries = Array.from(globalCache.entries());
      // Сортируем по времени создания (старые первыми)
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      // Удаляем самые старые записи
      const toDelete = entries.slice(0, globalCache.size - configRef.current.maxSize);
      toDelete.forEach(([key]) => globalCache.delete(key));
    }
  }, []);

  // Получение данных из кэша или API
  const fetchData = useCallback(async (forceRefresh = false): Promise<void> => {
    if (!isMountedRef.current) return;

    setLoading(true);
    setError(null);

    try {
      // Проверяем кэш, если не принудительное обновление
      if (!forceRefresh) {
        const cachedEntry = globalCache.get(key);
        if (cachedEntry && isCacheValid(cachedEntry)) {
          setData(cachedEntry.data);
          setLastUpdate(new Date(cachedEntry.timestamp));
          setLoading(false);
          return;
        }
      }

      // Получаем данные из API
      const freshData = await fetcher();
      
      if (!isMountedRef.current) return;

      // Сохраняем в кэш
      const cacheEntry: CacheEntry<T> = {
        data: freshData,
        timestamp: Date.now(),
        ttl: configRef.current.defaultTTL
      };
      
      globalCache.set(key, cacheEntry);
      
      // Очищаем устаревшие записи и ограничиваем размер
      cleanupExpiredEntries();
      enforceMaxSize();

      // Обновляем состояние
      setData(freshData);
      setLastUpdate(new Date());
      
    } catch (err) {
      if (!isMountedRef.current) return;
      
      const errorMessage = err instanceof Error ? err.message : String(i18n.t('common:errors.unknown'));
      setError(errorMessage);
      console.error(`[useAnalyticsCache] Ошибка загрузки данных для ключа "${key}":`, err);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [key, fetcher, isCacheValid, cleanupExpiredEntries, enforceMaxSize]);

  // Инициализация данных
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, ...dependencies]);

  // Настройка автообновления
  useEffect(() => {
    if (!configRef.current.enableAutoRefresh) return;

    // Очищаем предыдущий интервал
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Устанавливаем новый интервал (каждую минуту проверяем TTL)
    intervalRef.current = setInterval(() => {
      const cachedEntry = globalCache.get(key);
      if (cachedEntry && !isCacheValid(cachedEntry)) {
        fetchData();
      }
    }, 60 * 1000); // Проверяем каждую минуту

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [key, fetchData, isCacheValid]);

  // Функция принудительного обновления
  const refresh = useCallback(() => {
    fetchData(true);
  }, [fetchData]);

  // Функция очистки кэша
  const clearCache = useCallback(() => {
    globalCache.delete(key);
    setData(null);
    setLastUpdate(null);
  }, [key]);

  // Функция очистки всего кэша
  const clearAllCache = useCallback(() => {
    globalCache.clear();
    setData(null);
    setLastUpdate(null);
  }, []);

  // Получение статистики кэша
  const getCacheStats = useCallback(() => {
    const totalEntries = globalCache.size;
    const validEntries = Array.from(globalCache.values()).filter(isCacheValid).length;
    const expiredEntries = totalEntries - validEntries;
    
    return {
      totalEntries,
      validEntries,
      expiredEntries,
      cacheSize: globalCache.size,
      maxSize: configRef.current.maxSize
    };
  }, [isCacheValid]);

  return {
    data,
    loading,
    error,
    lastUpdate,
    refresh,
    clearCache,
    clearAllCache,
    getCacheStats
  };
}

// Утилиты для работы с кэшем
export const analyticsCacheUtils = {
  // Очистка всего кэша
  clearAll: () => globalCache.clear(),
  
  // Получение всех ключей кэша
  getKeys: () => Array.from(globalCache.keys()),
  
  // Получение статистики кэша
  getStats: () => ({
    totalEntries: globalCache.size,
    keys: Array.from(globalCache.keys())
  }),
  
  // Предзагрузка данных
  preload: async <T>(key: string, fetcher: () => Promise<T>, ttl?: number) => {
    try {
      const data = await fetcher();
      const cacheEntry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        ttl: ttl || DEFAULT_CONFIG.defaultTTL
      };
      globalCache.set(key, cacheEntry);
      return data;
    } catch (error) {
      console.error(`[analyticsCacheUtils] Ошибка предзагрузки для ключа "${key}":`, error);
      throw error;
    }
  }
};

export default useAnalyticsCache;
